import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";

import type { SalesStrategyDocument } from "@xop/dashboard-shared";

interface Version {
  id: string;
  version: number;
  created_at: string;
}

/**
 * Horizontal version picker. Sits on the row above the strategy memo
 * so the operator can see how many memos exist and step through them
 * without scrolling sideways. Each version renders as a chip showing
 * `v<n>` and a short date; the active chip is filled with the brand
 * colour. Presentational only — clicking notifies the parent via
 * `onPick`.
 */
export function StrategyVersionPicker({
  versions,
  active,
  onPick,
}: {
  versions: Version[];
  active: SalesStrategyDocument | null | undefined;
  onPick: (id: string) => void;
}): JSX.Element | null {
  if (versions.length === 0) return null;

  return (
    <Box
      component="nav"
      aria-label="Strategy versions"
      sx={{
        display: "grid",
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        px: 1.5,
        py: 1,
      }}
    >
      <Box component="header" sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1.5 }}>
        <Typography component="h2" sx={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
          Versions
        </Typography>
        <Box component="span" sx={{ fontSize: 10, color: "text.disabled" }}>
          {versions.length} memo{versions.length === 1 ? "" : "s"}
        </Box>
      </Box>
      <Box
        component="ul"
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "stretch",
          gap: 0.75,
          overflowX: "auto",
          pb: 0.25,
          listStyle: "none",
          m: 0,
          p: 0,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {versions.map((v) => {
          const isActive = active?.id === v.id;
          return (
            <Box component="li" key={v.id} sx={{ flexShrink: 0 }}>
              <ButtonBase
                onClick={() => onPick(v.id)}
                aria-current={isActive ? "true" : undefined}
                sx={[
                  {
                    display: "inline-flex",
                    alignItems: "baseline",
                    gap: 1,
                    borderRadius: 1,
                    border: 1,
                    px: 1.25,
                    py: 0.5,
                    textAlign: "left",
                    fontSize: 12,
                    transition: "color 150ms, background-color 150ms",
                  },
                  isActive
                    ? { borderColor: "primary.main", bgcolor: "primary.main", color: "primary.contrastText" }
                    : { borderColor: "divider", color: "text.secondary", "&:hover": { bgcolor: "surfaceSoft" } },
                ]}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>v{v.version}</Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: isActive
                      ? (t: Theme) => alpha(t.palette.primary.contrastText, 0.8)
                      : "text.disabled",
                  }}
                >
                  {new Date(v.created_at).toLocaleDateString()}
                </Box>
              </ButtonBase>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
